import './upload.scss';

import React from 'react';
import Button from 'hellospa/foundation/control/button';
import Dropzone from 'react-dropzone';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import ProgressIndicator from 'hellospa/foundation/control/progress-indicator';
import * as signatureTypes from './constants/signature-types';
import SignatureEditArea from './signature-edit-area';

const MAX_FILE_SIZE = 40 * 1000 * 1000; // 40 MB

const messages = defineMessages({
  buttonText: {
    id: '',
    description: 'Text for a button which opens the file chooser when clicked.',
    defaultMessage: 'Add photo',
  },
});

class Upload extends React.PureComponent {

  state = {
    showFileSizeError: false,
    showFileFormatError: false,
    isUploadingSignature: false,
    uploadProgress: 0,
  };

  signatureEditAreaRef = React.createRef();

  updateProgressInterval = null;

  onDropAccepted = ([file]) => {
    this.upload(file);

    // Clear errors
    this.setState({
      showFileSizeError: false,
      showFileFormatError: false,
    });
  };

  onDropRejected = ([file]) => {
    const showFileSizeError = file.size > MAX_FILE_SIZE;
    const showFileFormatError = !/image\/(png|jpg|jpeg|bmp|gif)/.test(file.type);

    this.setState({
      showFileSizeError,
      showFileFormatError,
    });
  };

  upload(file) {
    this.props.uploadSignature(file);

    this.setState({
      isUploadingSignature: true,
      uploadProgress: 0,
    });

    this.updateProgressInterval = setInterval(() => {
      this.setState(({ uploadProgress }) => ({
        uploadProgress: Math.min(0.9, uploadProgress + 0.005),
      }));
    }, 25);
  }

  componentDidUpdate(prevProps) {
    this.props.enableInsertButtonCallback(this.shouldEnableInsert());

    // We don't have any visibility into this, so we'll
    // have to make some assumptions based on the props
    // passed to this component.
    if (!prevProps.uploadedSignature && this.props.uploadedSignature) {
      if (this.updateProgressInterval) {
        clearInterval(this.updateProgressInterval);
      }

      this.setState({
        isUploadingSignature: false,
        uploadProgress: 0,
      });
    }
  }

  getSignatureData() {
    const signature = this.props.uploadedSignature;
    const editArea = this.signatureEditAreaRef.current;

    if (!editArea) return {};

    const isCropped = editArea.state.isCropped;

    const signatureData = {
      create_type_code: signatureTypes.UPLOAD,
      guid: signature.guid,
      threshold: editArea.state.contrastValue,
      degrees: editArea.state.degreesValue,
    };

    if (isCropped) {
      signatureData.is_cropped = true;
      signatureData.x = editArea.state.cropX;
      signatureData.y = editArea.state.cropY;
      signatureData.width = editArea.state.cropWidth;
      signatureData.height = editArea.state.cropHeight;
    } else {
      signatureData.width = signature.width;
      signatureData.height = signature.height;
    }

    return signatureData;
  }

  shouldEnableInsert() {
    return !!this.props.uploadedSignature;
  }

  renderDropzone() {
    return (
      <div>
        <Dropzone
          onDropAccepted={this.onDropAccepted}
          onDropRejected={this.onDropRejected}
          multiple={false}
          disabled={this.props.isUploadingSignature}
          accept="image/png, image/jpg, image/jpeg, image/bmp, image/gif"
          maxSize={MAX_FILE_SIZE}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className="signature-modal-upload__dropzone"
            >
              <input {...getInputProps()} />
              <Button
                className="signature-modal-upload__button whitelabel-primary-button"
                data-qa-ref="signing-modal--image-upload-button"
              >
                {this.props.intl.formatMessage(messages.buttonText)}
              </Button>

            </div>
          )}
        </Dropzone>
        <p {...this.state.showFileFormatError ? { style: { color: 'red' } } : {}}>
          <FormattedMessage
            id=""
            description="Text indicating acceptable file formats for an upload field."
            defaultMessage="Acceptable file formats: png, jpg, jpeg, bmp, gif" />
        </p>
        <p {...this.state.showFileSizeError ? { style: { color: 'red' } } : {}}>
          <FormattedMessage
            id=""
            description="Text indicating max acceptable file size for an upload field."
            defaultMessage="Max file size: 40 MB" />
        </p>
      </div>
    );
  }

  render() {
    const hasUploadedSignature = !!this.props.uploadedSignature;

    return (
      <div className="signature-modal-upload">
        {this.state.isUploadingSignature && (
          <div className="signature-modal-upload__progress">
            <ProgressIndicator
              progress={this.state.uploadProgress}
              showCancelButton={false} />
          </div>
        )}
        {(!this.state.isUploadingSignature && hasUploadedSignature) ? (
          <SignatureEditArea
            ref={this.signatureEditAreaRef}
            getSignatureUrl={this.props.getSignatureUrl}
            intl={this.props.intl}
            onRotate={this.props.onRotateSignature}
            signature={this.props.uploadedSignature}
            clearUploadedSignature={this.props.clearUploadedSignature}
            type={this.props.type} />
        ) : this.renderDropzone()}
      </div>
    );
  }
}

export default injectIntl(Upload, { forwardRef: true });
